import { ChevronDown, ChevronUp, List, Plus, Settings2, Trash2, X } from "lucide-react";
import { useState, type JSX } from "react";
import { Controller, useFieldArray, useFormContext, useWatch, type Control } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import type { IntentName } from "@/design";
import { cn } from "@/shared/lib/cn";
import { Select } from "@/shared/ui/select";

import { useGiftExpansion, useResolveSkuNames } from "../api";
import type {
  PromotionFormInput,
  PromotionFormValues,
  RuleProductValues,
} from "../lib/promotion-schema";

import { SegToggle } from "./builder-controls";
import { SkuPickerModal } from "./SkuPickerModal";

type ResolvedSku = components["schemas"]["ResolvedSku"];
type ProductKind = RuleProductValues["kind"];

interface RuleCardProps {
  index: number;
  control: Control<PromotionFormInput, unknown, PromotionFormValues>;
  onRemove: () => void;
  onMoveUp?: (() => void) | undefined;
  onMoveDown?: (() => void) | undefined;
  /**
   * View-only (spec §2): edit actions (trash, move, SKU pick, chip/gift remove,
   * add gift/exclude) are hidden and the rule body is disabled, but the
   * collapse toggle in the header stays usable.
   */
  readOnly?: boolean;
}

const MINI =
  "h-8 w-full rounded-md border border-[var(--premium-border-strong)] bg-white px-2 text-[13px] tabular-nums outline-none transition focus:border-[var(--brand-500)] focus-ring disabled:opacity-50";
const COL_TITLE =
  "mb-3 text-[10.5px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]";
const MINI_LABEL = "block text-[11px] font-semibold text-[var(--text-secondary)]";

const QUOTA_FILL: Record<IntentName, string> = {
  success: "bg-[var(--intent-success-fg)]",
  warning: "bg-[var(--intent-warning-fg)]",
  danger: "bg-[var(--intent-danger-fg)]",
  info: "bg-[var(--intent-info-fg)]",
  neutral: "bg-[var(--intent-neutral-fg)]",
};

interface RuleQuotaBarProps {
  used: number;
  /** undefined/null when the rule is uncapped (gift_limit_quantity = 0). */
  remaining: number | null | undefined;
  /** Editable cap from the form; 0/blank means uncapped. */
  cap: number | undefined;
  index: number;
}

/**
 * Per-rule gift usage (mockup .quota-mini): Used / Remaining + a thin progress
 * bar, reusing the same intent tokens as QuotaProgressBar. Uncapped rules
 * (no `remaining` / cap 0) render "No limit" instead of a bar.
 */
function RuleQuotaBar({ used, remaining, cap, index }: RuleQuotaBarProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const capped = remaining != null && cap != null && cap > 0;

  if (!capped) {
    return (
      <p
        className="mt-2 text-[11px] font-semibold text-[var(--text-subtle)]"
        data-testid={`rule-quota-${index}`}
        data-uncapped="true"
      >
        {t("builder.quota_used")}: <span className="tabular-nums">{used.toLocaleString()}</span> ·{" "}
        {t("builder.quota_no_limit")}
      </p>
    );
  }

  const total = used + remaining;
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const exhausted = remaining <= 0;
  const low = !exhausted && total > 0 && remaining / total < 0.1;
  const intent: IntentName = exhausted ? "danger" : low ? "warning" : "success";

  return (
    <div
      className="mt-2 flex flex-col gap-1"
      data-testid={`rule-quota-${index}`}
      data-intent={intent}
      data-uncapped="false"
    >
      <span className="text-[11px] text-[var(--text-secondary)]">
        {t("builder.quota_used")}:{" "}
        <strong className="font-semibold tabular-nums">{used.toLocaleString()}</strong> ·{" "}
        {t("builder.quota_remaining")}:{" "}
        <strong className="font-semibold tabular-nums">{remaining.toLocaleString()}</strong>
      </span>
      <div className="h-[5px] w-full max-w-[200px] overflow-hidden rounded-full bg-[var(--surface-sunken)]">
        <div
          className={cn("h-full rounded-full transition-all", QUOTA_FILL[intent])}
          style={{ width: `${pct}%` }}
          data-testid={`rule-quota-fill-${index}`}
        />
      </div>
      <span className="text-[11px] font-semibold tabular-nums text-[var(--text-subtle)]">
        {t("builder.quota_pct_used", { pct })}
      </span>
    </div>
  );
}

/** SKU code (mono) + optional product name (regular, en-dash separator via CSS). */
function SkuLabel({ sku, name }: { sku: string; name: string | undefined }): JSX.Element {
  return (
    <>
      <span className="font-mono">{sku}</span>
      {name && (
        <span className="font-normal before:mx-1 before:opacity-60 before:content-['–']">
          {name}
        </span>
      )}
    </>
  );
}

export function RuleCard({
  index,
  control,
  onRemove,
  onMoveUp,
  onMoveDown,
  readOnly = false,
}: RuleCardProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const {
    register,
    formState: { errors },
  } = useFormContext<PromotionFormInput, unknown, PromotionFormValues>();
  const ruleErrors = errors.rules?.[index];
  const giftLimitError = ruleErrors?.gift_limit_quantity?.message;
  const giftUsedOffsetError = ruleErrors?.gift_used_offset?.message;
  const productsError = (ruleErrors?.products as { message?: string } | undefined)?.message;
  const dangerRing =
    "border-[var(--intent-danger-border)] focus:border-[var(--intent-danger-border)]";
  const [collapsed, setCollapsed] = useState(false);
  const [picker, setPicker] = useState<{ kind: ProductKind; mode: "single" | "multi" } | null>(
    null,
  );

  const products = useFieldArray({ control, name: `rules.${index}.products` });
  const isMinSpend = useWatch({ control, name: `rules.${index}.is_min_spend` });
  const giftLimit = useWatch({ control, name: `rules.${index}.gift_limit_quantity` });
  const giftUsed = useWatch({ control, name: `rules.${index}.gift_used` });
  const giftRemaining = useWatch({ control, name: `rules.${index}.gift_remaining` });
  const giftUsedOffset = useWatch({ control, name: `rules.${index}.gift_used_offset` });
  // Collapsed by default — this field only matters when migrating a promotion
  // from a legacy system, so most operators never touch it. A rule loaded
  // with a nonzero offset (e.g. an already-migrated promotion) starts expanded
  // so the quota math it affects isn't silently hidden.
  const [showLegacyOffset, setShowLegacyOffset] = useState(() => (giftUsedOffset ?? 0) > 0);

  // sino-parity: no all/specific toggle. The trigger is "All SKU" (storewide)
  // when no trigger products are picked, else "SKU (N)". The picker icons
  // add/remove trigger products; this count drives the display.
  const triggerCount = products.fields.filter(
    (f) => (f as { kind: string }).kind === "trigger",
  ).length;

  // Preview what stock the gift reserves: a gift that is a listing/bundle expands
  // to its base components × multiplicity (sino multi-pack). A single-SKU gift has
  // no listing → no expansion. Presentational only.
  const giftSku = productRows("add_on")[0]?.field.sku ?? null;
  const giftExpansion = useGiftExpansion(giftSku);
  const giftComponents = giftExpansion.data;

  // Resolve this rule's SKU codes to product names so the trigger/gift chips can
  // read "SKU – Name". Presentational only — never persisted or sent on save.
  const nameForSku = useResolveSkuNames(products.fields.map((f) => (f as { sku: string }).sku));

  function addProducts(skus: ResolvedSku[], kind: ProductKind): void {
    for (const s of skus) {
      // Dedup by (kind, sku) — sino skips a gift already in add_on_product.
      if (products.fields.some((f) => f.kind === kind && (f as { sku: string }).sku === s.sku)) {
        continue;
      }
      products.append({
        sku: s.sku,
        kind,
        quantity: 1,
        is_freebie: kind === "add_on",
      });
    }
  }

  function productRows(kind: ProductKind): { field: { id: string; sku: string }; i: number }[] {
    return products.fields
      .map((field, i) => ({ field: field as { id: string; sku: string }, i }))
      .filter((r) => products.fields[r.i]!.kind === kind);
  }

  function removeByKindRow(kind: ProductKind, rowSku: string): void {
    const target = products.fields.findIndex((f) => f.kind === kind && f.sku === rowSku);
    if (target >= 0) products.remove(target);
  }

  // SKU chips (mockup .sku-chip): used for trigger + exclude-from-min-spend lists.
  // `withQty` surfaces the per-product `quantity` (how many of this trigger SKU
  // must be bought) as a small inline input — sino "Buy N ×" framing.
  function renderChips(kind: ProductKind, withQty = false): JSX.Element {
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={`rule-${kind}-list-${index}`}>
        {productRows(kind).map(({ field, i }) => (
          <span
            key={field.id}
            className="inline-flex max-w-[260px] items-center gap-1 rounded-full border border-[var(--brand-200,var(--brand-100))] bg-[var(--row-selected)] px-2.5 py-1 text-[12px] font-semibold text-[var(--brand-700)]"
          >
            <span className="min-w-0 truncate">
              <SkuLabel sku={field.sku} name={nameForSku[field.sku]} />
            </span>
            {withQty && (
              <span className="ml-0.5 inline-flex items-center gap-0.5 text-[var(--text-subtle)]">
                <span aria-hidden className="text-[11px] opacity-70">
                  ×
                </span>
                <input
                  type="number"
                  min={1}
                  aria-label={t("detail.field.quantity")}
                  data-testid={`rule-${kind}-qty-${index}`}
                  className="h-5 w-10 rounded border border-[var(--premium-border-strong)] bg-white px-1 text-[11px] tabular-nums text-[var(--text-primary)] outline-none focus:border-[var(--brand-500)] focus-ring"
                  {...register(`rules.${index}.products.${i}.quantity`, { valueAsNumber: true })}
                />
              </span>
            )}
            {!readOnly && (
              <button
                type="button"
                aria-label={t("action.delete")}
                data-testid={`rule-${kind}-remove-${index}`}
                className="text-[var(--brand-700)] opacity-60 transition hover:opacity-100"
                onClick={() => removeByKindRow(kind, field.sku)}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            )}
          </span>
        ))}
      </div>
    );
  }

  // Gift line (mockup .gift-line): SKU + remove, in a bordered row.
  function renderGiftLines(): JSX.Element {
    return (
      <div
        className={cn("space-y-1.5", productsError && "rounded-lg border p-1.5 " + dangerRing)}
        data-testid={`rule-add_on-list-${index}`}
        aria-invalid={productsError ? "true" : undefined}
      >
        {productRows("add_on").map(({ field, i }) => (
          <div
            key={field.id}
            className="flex items-center gap-2 rounded-lg border border-[var(--premium-border)] bg-[var(--canvas)] px-2.5 py-1.5"
          >
            <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-[var(--brand-700)]">
              <SkuLabel sku={field.sku} name={nameForSku[field.sku]} />
            </span>
            {/* sino-parity: gifts carry a per-gift quantity (item_gift.quantity). */}
            <span className="flex items-center gap-0.5 text-[var(--text-subtle)]">
              <span aria-hidden className="text-[11px] opacity-70">
                ×
              </span>
              <input
                type="number"
                min={1}
                aria-label={t("detail.field.quantity")}
                data-testid={`rule-add_on-qty-${index}`}
                className="h-5 w-10 rounded border border-[var(--premium-border-strong)] bg-white px-1 text-[11px] tabular-nums text-[var(--text-primary)] outline-none focus:border-[var(--brand-500)] focus-ring"
                {...register(`rules.${index}.products.${i}.quantity`, { valueAsNumber: true })}
              />
            </span>
            {!readOnly && (
              <button
                type="button"
                aria-label={t("action.delete")}
                data-testid={`rule-add_on-remove-${index}`}
                className="grid h-6 w-6 place-items-center rounded text-[var(--text-subtle)] transition hover:bg-[var(--intent-danger-bg)] hover:text-[var(--intent-danger-fg)]"
                onClick={() => removeByKindRow("add_on", field.sku)}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </div>
        ))}
      </div>
    );
  }

  const addBtn =
    "inline-flex items-center gap-1 rounded-md border border-[var(--premium-border-strong)] bg-white px-2.5 py-1 text-[12px] font-semibold text-[var(--text-secondary)] shadow-[var(--sh-xs)] transition hover:bg-[var(--canvas)]";

  return (
    <div
      data-testid="rule-card"
      className="overflow-hidden rounded-xl border border-[var(--premium-border)] bg-white shadow-[var(--sh-sm)]"
    >
      {/* Top bar (mockup .rule-top) */}
      <div className="flex items-center gap-2 border-b border-[var(--premium-border)] bg-[var(--canvas)] px-4 py-2.5">
        <span className="text-[13px] font-bold text-[var(--text-primary)]">
          {t("rule.title", { n: index + 1 })}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {!readOnly && (
            <button
              type="button"
              aria-label={t("rule.move_up")}
              data-testid="rule-move-up"
              disabled={!onMoveUp}
              onClick={onMoveUp}
              className="grid h-7 w-7 place-items-center rounded-md border border-[var(--premium-border-strong)] bg-white text-[var(--text-subtle)] transition hover:bg-[var(--canvas)] disabled:opacity-30"
            >
              <ChevronUp className="h-4 w-4" aria-hidden />
            </button>
          )}
          {!readOnly && (
            <button
              type="button"
              aria-label={t("rule.move_down")}
              data-testid="rule-move-down"
              disabled={!onMoveDown}
              onClick={onMoveDown}
              className="grid h-7 w-7 place-items-center rounded-md border border-[var(--premium-border-strong)] bg-white text-[var(--text-subtle)] transition hover:bg-[var(--canvas)] disabled:opacity-30"
            >
              <ChevronDown className="h-4 w-4" aria-hidden />
            </button>
          )}
          <button
            type="button"
            aria-label={collapsed ? t("rule.expand") : t("rule.collapse")}
            data-testid="rule-collapse"
            onClick={() => setCollapsed((c) => !c)}
            className="grid h-7 w-7 place-items-center rounded-md border border-[var(--premium-border-strong)] bg-white text-[var(--text-subtle)] transition hover:bg-[var(--canvas)]"
          >
            {collapsed ? (
              <ChevronDown className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronUp className="h-4 w-4" aria-hidden />
            )}
          </button>
          {!readOnly && (
            <button
              type="button"
              aria-label={t("action.delete")}
              data-testid="rule-remove"
              onClick={onRemove}
              className="grid h-7 w-7 place-items-center rounded-md border border-[var(--premium-border-strong)] bg-white text-[var(--intent-danger-fg)] transition hover:bg-[var(--intent-danger-bg)]"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>

      {!collapsed && (
        <fieldset disabled={readOnly} className="m-0 min-w-0 border-0 p-0">
          <div className="grid md:grid-cols-3">
            {/* ── Trigger column ── */}
            <section className="space-y-3.5 border-b border-[var(--premium-border)] p-4 md:border-b-0 md:border-r">
              <h4 className={COL_TITLE}>{t("detail.column.trigger")}</h4>
              <div>
                <label className={`${MINI_LABEL} mb-1`}>{t("detail.field.payment_method")}</label>
                <Select
                  data-testid={`rule-payment-method-${index}`}
                  className="h-8 w-full text-[12px]"
                  {...register(`rules.${index}.payment_method`)}
                >
                  <option value="ALL">{t("detail.payment.any")}</option>
                  <option value="COD">{t("detail.payment.cod")}</option>
                  <option value="NON_COD">{t("detail.payment.non_cod")}</option>
                </Select>
              </div>
              {/* Trigger Product (sino-style): "All SKU" until SKUs are picked via
                the product (gear) or SKU (list) modal, then "SKU (N)". */}
              <div>
                <label className={`${MINI_LABEL} mb-1.5`}>
                  {t("detail.field.trigger_product")}
                </label>
                <div className="flex items-stretch gap-1.5">
                  <div
                    data-testid={`rule-trigger-product-display-${index}`}
                    className="flex h-8 flex-1 items-center rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-[var(--canvas)] px-2.5 text-[12px] font-semibold text-[var(--text-primary)]"
                  >
                    {triggerCount > 0
                      ? t("builder.trigger_sku_count", { count: triggerCount })
                      : t("builder.trigger_type.all")}
                  </div>
                  {!readOnly && (
                    <button
                      type="button"
                      data-testid={`rule-trigger-pick-product-${index}`}
                      aria-label={t("builder.pick_by_product")}
                      onClick={() => setPicker({ kind: "trigger", mode: "multi" })}
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-[var(--control-r)] border border-[var(--premium-border-strong)] text-[var(--text-subtle)] transition hover:bg-[var(--canvas)]"
                    >
                      <Settings2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  )}
                  {!readOnly && (
                    <button
                      type="button"
                      data-testid={`rule-trigger-pick-sku-${index}`}
                      aria-label={t("builder.pick_by_sku")}
                      onClick={() => setPicker({ kind: "trigger", mode: "multi" })}
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-[var(--control-r)] border border-[var(--premium-border-strong)] text-[var(--text-subtle)] transition hover:bg-[var(--canvas)]"
                    >
                      <List className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  )}
                </div>
                {/* sino-parity: trigger products are a SKU list with NO per-product
                  quantity (handleTriggerProductChangeModal sets no quantity). */}
                <div className={triggerCount > 0 ? "mt-1.5" : ""}>
                  {renderChips("trigger", false)}
                </div>
              </div>
              <label className="flex items-center gap-2 text-[12px] font-semibold text-[var(--text-secondary)]">
                <input
                  type="checkbox"
                  data-testid={`rule-is-min-spend-${index}`}
                  {...register(`rules.${index}.is_min_spend`)}
                />
                {t("builder.is_min_spend")}
              </label>
              {isMinSpend && (
                <div>
                  <label className={`${MINI_LABEL} mb-1`}>{t("builder.min_spend_value")}</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    data-testid={`rule-min-spend-value-${index}`}
                    className={`${MINI} max-w-[120px]`}
                    {...register(`rules.${index}.min_spend_value`)}
                  />
                </div>
              )}
            </section>

            {/* ── Add-on (gift) column ── */}
            <section className="space-y-3.5 border-b border-[var(--premium-border)] p-4 md:border-b-0 md:border-r">
              <h4 className={COL_TITLE}>{t("detail.column.gift_addon")}</h4>
              <div>
                <label className={`${MINI_LABEL} mb-1.5`}>{t("detail.field.gift_sku")}</label>
                {renderGiftLines()}
                {giftSku && giftExpansion.isSuccess && giftComponents?.length === 0 && (
                  <p
                    data-testid={`rule-gift-single-sku-hint-${index}`}
                    className="mt-1 text-[12px] text-[var(--text-secondary)]"
                  >
                    {t("builder.add_on.single_sku_hint")}
                  </p>
                )}
                {productsError && (
                  <p
                    data-testid={`rule-error-products-${index}`}
                    className="mt-1 text-[12px] text-[var(--intent-danger-fg)]"
                  >
                    {t(productsError)}
                  </p>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    data-testid={`rule-add-gift-${index}`}
                    onClick={() => setPicker({ kind: "add_on", mode: "multi" })}
                    className={`mt-1.5 ${addBtn}`}
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden /> {t("rule.add_gift")}
                  </button>
                )}
              </div>
              {giftComponents && giftComponents.length > 0 && (
                <div
                  data-testid={`rule-gift-expansion-${index}`}
                  className="rounded-lg border border-dashed border-[var(--premium-border-strong)] bg-[var(--canvas)] px-2.5 py-2"
                >
                  <span className="mb-1 block text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-subtle)]">
                    {t("builder.gift_expands_to")}
                  </span>
                  <ul className="space-y-0.5">
                    {giftComponents.map((c) => (
                      <li
                        key={c.base_sino_sku}
                        className="flex items-center justify-between text-[12px] text-[var(--text-secondary)]"
                      >
                        <span className="font-mono">{c.base_sino_sku}</span>
                        <span className="tabular-nums text-[var(--text-subtle)]">
                          × {c.multiplicity}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* ── Constraint column ── */}
            <section className="space-y-3.5 p-4">
              <h4 className={COL_TITLE}>{t("detail.column.constraints")}</h4>
              <div>
                <label className={`${MINI_LABEL} mb-1`}>{t("builder.gift_limit_quantity")}</label>
                <input
                  type="number"
                  min={1}
                  data-testid={`rule-gift-limit-quantity-${index}`}
                  aria-invalid={giftLimitError ? "true" : undefined}
                  className={cn(MINI, "max-w-[110px]", giftLimitError && dangerRing)}
                  {...register(`rules.${index}.gift_limit_quantity`, { valueAsNumber: true })}
                />
                {giftLimitError && (
                  <p
                    data-testid={`rule-error-gift-limit-quantity-${index}`}
                    className="mt-1 text-[12px] text-[var(--intent-danger-fg)]"
                  >
                    {t(giftLimitError)}
                  </p>
                )}
                {/* Per-rule quota usage (mockup .quota-mini). Read-only — hydrated
                  from the detail payload. Uncapped rules show "No limit". */}
                {giftUsed != null && (
                  <RuleQuotaBar
                    used={giftUsed}
                    remaining={giftRemaining}
                    cap={giftLimit}
                    index={index}
                  />
                )}
              </div>
              <div>
                {showLegacyOffset ? (
                  <>
                    <label className={`${MINI_LABEL} mb-1`}>{t("builder.gift_used_offset")}</label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      aria-invalid={giftUsedOffsetError ? "true" : undefined}
                      className={cn(MINI, "max-w-[110px]", giftUsedOffsetError && dangerRing)}
                      {...register(`rules.${index}.gift_used_offset`, {
                        // ponytail: setValueAs over valueAsNumber — an emptied input coerces
                        // to 0 (passes validation) instead of NaN (silently blocks submit).
                        setValueAs: (v) => (v === "" ? 0 : Number(v)),
                      })}
                      data-testid={`rule-${index}-gift-used-offset`}
                    />
                    {giftUsedOffsetError ? (
                      <p
                        data-testid={`rule-error-gift-used-offset-${index}`}
                        className="mt-1 text-[12px] text-[var(--intent-danger-fg)]"
                      >
                        {t(giftUsedOffsetError)}
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-[var(--text-subtle)]">
                        {t("builder.gift_used_offset_hint")}
                      </p>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowLegacyOffset(true)}
                    className="flex items-center gap-1 text-[11px] font-semibold text-[var(--text-subtle)] transition hover:text-[var(--text-secondary)]"
                    data-testid={`rule-${index}-gift-used-offset-toggle`}
                  >
                    <Settings2 className="h-3 w-3" aria-hidden />
                    {t("builder.gift_used_offset_toggle")}
                  </button>
                )}
              </div>
              <div>
                <label className={`${MINI_LABEL} mb-1.5`}>
                  {t("builder.gift_limit_type.label")}
                </label>
                <Controller
                  control={control}
                  name={`rules.${index}.gift_limit_type`}
                  render={({ field }) => (
                    <SegToggle
                      aria-label={t("builder.gift_limit_type.label")}
                      data-testid={`rule-gift-limit-type-${index}`}
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { value: "PER_ORDER", label: t("builder.gift_limit_type.per_order_short") },
                        { value: "PER_ITEM", label: t("builder.gift_limit_type.per_item_short") },
                      ]}
                    />
                  )}
                />
              </div>
              {/* ponytail: gates three min-spend-basis controls on the existing isMinSpend watch */}
              {isMinSpend && (
                <>
                  <div>
                    <label className={`${MINI_LABEL} mb-1.5`}>{t("builder.discount.label")}</label>
                    <Controller
                      control={control}
                      name={`rules.${index}.discount`}
                      render={({ field }) => (
                        <SegToggle
                          aria-label={t("builder.discount.label")}
                          data-testid={`rule-discount-${index}`}
                          value={field.value}
                          onChange={field.onChange}
                          options={[
                            { value: "include", label: t("builder.discount.include_short") },
                            { value: "exclude", label: t("builder.discount.exclude_short") },
                          ]}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <label className={`${MINI_LABEL} mb-1.5`}>
                      {t("builder.shipping_fee.label")}
                    </label>
                    <Controller
                      control={control}
                      name={`rules.${index}.shipping_fee`}
                      render={({ field }) => (
                        <SegToggle
                          aria-label={t("builder.shipping_fee.label")}
                          data-testid={`rule-shipping-fee-${index}`}
                          value={field.value}
                          onChange={field.onChange}
                          options={[
                            { value: "include", label: t("builder.shipping_fee.include_short") },
                            { value: "exclude", label: t("builder.shipping_fee.exclude_short") },
                          ]}
                        />
                      )}
                    />
                  </div>
                  <div>
                    <label className={`${MINI_LABEL} mb-1.5`}>{t("rule.exclude_min_spend")}</label>
                    {renderChips("exclude_min_spend")}
                    {!readOnly && (
                      <button
                        type="button"
                        data-testid={`rule-add-exclude-${index}`}
                        onClick={() => setPicker({ kind: "exclude_min_spend", mode: "multi" })}
                        className={`mt-1.5 ${addBtn}`}
                      >
                        <Plus className="h-3.5 w-3.5" aria-hidden /> {t("rule.add_sku")}
                      </button>
                    )}
                  </div>
                </>
              )}
            </section>
          </div>
        </fieldset>
      )}

      {/* Hidden registered fields kept in sync (sequence + rule_type). */}
      <Controller
        control={control}
        name={`rules.${index}.sequence`}
        render={({ field }) => <input type="hidden" {...field} value={field.value ?? index} />}
      />

      {picker && (
        <SkuPickerModal
          open
          mode={picker.mode}
          onClose={() => setPicker(null)}
          alreadySelected={products.fields
            .filter((f) => f.kind === picker.kind)
            .map((f) => (f as { sku: string }).sku)}
          onConfirm={(skus) => {
            if (picker.mode === "single") {
              // gift is single-select: replace existing add_on rows.
              for (let i = products.fields.length - 1; i >= 0; i--) {
                if (products.fields[i]!.kind === picker.kind) products.remove(i);
              }
            }
            addProducts(skus, picker.kind);
            setPicker(null);
          }}
        />
      )}
    </div>
  );
}
